import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import placeholderImage from '../assets/images/placeholder.jpg';

// Import mobile placeholder images
import pathPlanningMobile from '../assets/images/mobileVideoPlaceholder/PathPlanning_pathPlanning_mobile.jpg';
import realStackingMobile from '../assets/images/mobileVideoPlaceholder/RealRobotArm_realStacking_mobile.jpg';
import pennAirVideoMobile from '../assets/images/mobileVideoPlaceholder/AerialRobotics_PennAirVideo_mobile.jpg';
import sudokuMobile from '../assets/images/mobileVideoPlaceholder/Sudoku_soduko_mobile.jpg';
import pendulumStateSpaceMobile from '../assets/images/mobileVideoPlaceholder/PendulumSwingUp_stateSpace_mobile.jpg';
import pendulumSwingUpMobile from '../assets/images/mobileVideoPlaceholder/PendulumSwingUp_SwingUp_mobile.jpg';
import robotManipulatorMobile from '../assets/images/mobileVideoPlaceholder/RobotManipulator_RobotManipulator_mobile.jpg';
import tournamentMobile from '../assets/images/mobileVideoPlaceholder/RobotManipulator_tournament_mobile.jpg';
import sfmMobile from '../assets/images/mobileVideoPlaceholder/Sfm_sfm_mobile.jpg';
import SDGMobile from '../assets/images/mobileVideoPlaceholder/SDG_SDG_mobile.jpg';

// Import video poster images (for desktop videos)
import pathPlanningVideoPoster from '../assets/images/posters/PathPlanning_pathPlanning_poster.jpg';
import realStackingVideoPoster from '../assets/images/posters/RealRobotArm_stackingShort_poster.jpg';
import pennAirVideoVideoPoster from '../assets/images/posters/AerialRobotics_PennAirVideo_poster.jpg';
import sudokuVideoPoster from '../assets/images/posters/Sudoku_soduko_poster.jpg';
import pendulumStateSpaceVideoPoster from '../assets/images/posters/PendulumSwingUp_stateSpace_poster.jpg';
import pendulumSwingUpVideoPoster from '../assets/images/posters/PendulumSwingUp_SwingUp_poster.jpg';
import robotManipulatorVideoPoster from '../assets/images/posters/RobotManipulator_RobotManipulator_poster.jpg';
import tournamentVideoPoster from '../assets/images/posters/RobotManipulator_tournament_poster.jpg';
import sfmVideoPoster from '../assets/images/posters/Sfm_sfm_poster.jpg';
import SDGPoster from '../assets/images/posters/SDG_SDG_poster.jpg';

interface ProjectCardProps {
  id: number;
  title: string;
  imageUrl: string;
  description: string;
  link?: string;
  startTime?: number;
  endTime?: number;
  showHoverIndicator?: boolean;
}

const Card = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  height: 0;
  padding-bottom: 75%; /* Creates a 4:3 aspect ratio (landscape) */
  background-color: #ffffff;
  
  &:hover .overlay {
    opacity: 1;
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  ${Card}:hover img,
  ${Card}:hover video {
    transform: scale(1.05);
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: #333;
  text-align: center;
  
  h3 {
    font-size: 1.5rem;
    font-weight: 300;
  }
`;

const HoverIndicator = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 0, 0, 0.25);
  color: white;
  padding: 8px;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s ease;
  z-index: 10;
  opacity: 0.7;
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  ${Card}:hover & {
    opacity: 0;
  }
`;

const ProjectCard: React.FC<ProjectCardProps> = ({ 
  id, 
  title, 
  imageUrl, 
  description, 
  link, 
  startTime = 1, 
  endTime = 5,
  showHoverIndicator = false,
}) => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const isVideo = typeof imageUrl === 'string' && 
    (imageUrl.endsWith('.mp4') || imageUrl.endsWith('.webm') || imageUrl.endsWith('.ogg'));
  
  // Function to get the appropriate static image for mobile
  const getMobileImage = (videoUrl: string) => {
    if (videoUrl.includes('pathPlanning')) return pathPlanningMobile;
    if (videoUrl.includes('realStacking') || videoUrl.includes('stackingShort')) return realStackingMobile;
    if (videoUrl.includes('PennAirVideo')) return pennAirVideoMobile;
    if (videoUrl.includes('soduko')) return sudokuMobile;
    if (videoUrl.includes('stateSpace')) return pendulumStateSpaceMobile;
    if (videoUrl.includes('SwingUp')) return pendulumSwingUpMobile;
    if (videoUrl.includes('RobotManipulator')) return robotManipulatorMobile;
    if (videoUrl.includes('tournament')) return tournamentMobile;
    if (videoUrl.includes('sfm')) return sfmMobile;
    if (videoUrl.includes('mustardDemo')) return SDGMobile; // Using placeholder for SDG video
    return placeholderImage; // Fallback to placeholder if no match
  };
  
  // Function to get the appropriate video poster image (first frame)
  const getVideoPosterImage = (videoUrl: string) => {
    if (videoUrl.includes('pathPlanning')) return pathPlanningVideoPoster;
    if (videoUrl.includes('realStacking') || videoUrl.includes('stackingShort')) return realStackingVideoPoster;
    if (videoUrl.includes('PennAirVideo')) return pennAirVideoVideoPoster;
    if (videoUrl.includes('soduko')) return sudokuVideoPoster;
    if (videoUrl.includes('stateSpace')) return pendulumStateSpaceVideoPoster;
    if (videoUrl.includes('SwingUp')) return pendulumSwingUpVideoPoster;
    if (videoUrl.includes('RobotManipulator')) return robotManipulatorVideoPoster;
    if (videoUrl.includes('tournament')) return tournamentVideoPoster;
    if (videoUrl.includes('sfm')) return sfmVideoPoster;
    if (videoUrl.includes('mustardDemo')) return SDGPoster; // Using placeholder for SDG video
    return placeholderImage; // Fallback to placeholder if no match
  };
  
  // Get the appropriate images
  const mobileImage = isVideo ? getMobileImage(imageUrl) : placeholderImage;
  const videoPosterImage = isVideo ? getVideoPosterImage(imageUrl) : placeholderImage;
  
  // Check if the device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Check on resize
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Set up video element to play
  useEffect(() => {
    if (isVideo && videoRef.current) {
      const video = videoRef.current;
      
      // Set the initial time when the video is loaded
      const handleLoadedData = () => {
        video.currentTime = startTime;
        
        // iOS has strict autoplay policies
        if (isMobile) {
          // Don't try to autoplay on mobile devices
          video.pause();
        } else {
          // Don't autoplay until hovered on desktop
          video.pause();
        }
      };
      
      // Loop
      const handleTimeUpdate = () => {
        if (video.currentTime >= endTime) {
          video.currentTime = startTime;
        }
      };
      
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('timeupdate', handleTimeUpdate);
      
      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, [isVideo, startTime, endTime, isMobile]);
  
  // Control video playback based on hover state (only for non-mobile)
  useEffect(() => {
    if (isVideo && videoRef.current && !isMobile) {
      if (isHovered) {
        videoRef.current.play().catch(error => {
          console.log('Video play failed:', error);
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isHovered, isVideo, isMobile]);

  const handleClick = () => {
    if (link) {
      window.open(link, '_blank');
    } else {
      navigate(`/projects/${id}`);
    }
  };
  
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <Card 
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
    >
      {showHoverIndicator && !isMobile && (
        <HoverIndicator>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="0" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"></circle>
            <polygon points="10,8 16,12 10,16" fill="currentColor"></polygon>
          </svg>
        </HoverIndicator>
      )}
      <ImageContainer>
        {isVideo ? (
          isMobile ? (
            <img src={mobileImage} alt={title} />
          ) : (
            <video
              ref={videoRef}
              muted
              loop
              playsInline
              poster={videoPosterImage}
            >
              <source src={imageUrl} type="video/mp4" />
              <img src={mobileImage} alt={title} />
            </video>
          )
        ) : (
          <img src={imageUrl} alt={title} />
        )}
      </ImageContainer>
      {/* <Overlay className="overlay">
        <h3>{title}</h3>
      </Overlay> */}
    </Card>
  );
};

export default ProjectCard; 